import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { formatDuration } from "../../../helper/formatDuration";

const PlaylistGrid = ({
  playlists,
  selectedPlaylists,
  onPlaylistToggle,
  onSelectAll,
  platform,
  isDestination = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  const filteredPlaylists = playlists
    .filter((playlist) => {
      const matchesSearch =
        playlist?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        playlist?.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterType === "all" ||
        (filterType === "public" && playlist.public) ||
        (filterType === "private" && !playlist.public) ||
        (filterType === "owned" && playlist.owner === "You");
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "tracks":
          return b.trackCount - a.trackCount;
        case "created":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "duration":
          return b.duration - a.duration;
        default:
          return 0;
      }
    });

  const allSelected =
    filteredPlaylists.length > 0 &&
    filteredPlaylists.every((playlist) =>
      selectedPlaylists.includes(playlist.id)
    );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-text-primary mb-2">
          {isDestination ? "Select Destination" : "Select Source Playlists"}
        </h2>
        <p className="text-text-secondary">
          {isDestination
            ? "Choose where to sync your playlists"
            : `Choose playlists from ${
                platform === "spotify" ? "Spotify" : "YouTube Music"
              }`}
        </p>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search playlists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="min-w-[120px] px-3 py-2 bg-surface border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Playlists</option>
              <option value="owned">My Playlists</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 bg-surface border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="name">Name</option>
              <option value="tracks">Track Count</option>
              <option value="created">Date Created</option>
              <option value="duration">Duration</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {!isDestination && filteredPlaylists.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-surface-secondary rounded-lg">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => onSelectAll(!allSelected)}
                className="flex items-center space-x-2 text-sm font-medium text-primary hover:text-primary-600 transition-colors"
              >
                <div
                  className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                    allSelected ? "bg-primary border-primary" : "border-border"
                  }`}
                >
                  {allSelected && (
                    <Icon
                      name="Check"
                      size={12}
                      className="text-primary-foreground"
                    />
                  )}
                </div>
                <span>{allSelected ? "Deselect All" : "Select All"}</span>
              </button>
            </div>

            <div className="text-sm text-text-secondary">
              {selectedPlaylists.length} of {filteredPlaylists.length} selected
            </div>
          </div>
        )}
      </div>

      {/* Playlist Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPlaylists.map((playlist) => {
          const isSelected = selectedPlaylists.includes(playlist.id);
          return (
            <div
              key={playlist.id}
              onClick={() => onPlaylistToggle(playlist.id)}
              className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                isSelected
                  ? "border-primary bg-primary/10 shadow-elevation-2"
                  : "border-border bg-surface hover:bg-surface-secondary hover:border-border-secondary"
              }`}
            >
              {/* Selection Indicator */}
              {!isDestination && (
                <div className="absolute top-3 right-3 z-10">
                  <div
                    className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${
                      isSelected
                        ? "bg-primary border-primary"
                        : "border-border bg-surface"
                    }`}
                  >
                    {isSelected && (
                      <Icon
                        name="Check"
                        size={12}
                        className="text-primary-foreground"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Playlist Cover */}
              <div className="relative mb-3">
                <div className="aspect-square rounded-lg overflow-hidden bg-surface-secondary">
                  <Image
                    src={playlist.coverUrl}
                    alt={playlist.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Platform Badge */}
                <div className="absolute bottom-2 left-2">
                  <div
                    className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                      platform === "spotify"
                        ? "bg-success/20 text-success"
                        : "bg-error/20 text-error"
                    }`}
                  >
                    <Icon
                      name={platform === "spotify" ? "Music" : "Play"}
                      size={12}
                    />
                    <span className="capitalize">{platform}</span>
                  </div>
                </div>
              </div>

              {/* Playlist Info */}
              <div className="space-y-2">
                <h3 className="font-medium text-text-primary line-clamp-2 leading-tight">
                  {playlist.name}
                </h3>

                <p className="text-sm text-text-secondary line-clamp-2">
                  {playlist.description || "No description"}
                </p>

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-text-muted">
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center space-x-1">
                      <Icon name="Music" size={12} />
                      <span>{playlist.trackCount} tracks</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{formatDuration(playlist.duration)}</span>
                    </span>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Icon name={playlist.public ? "Globe" : "Lock"} size={12} />
                    <span>{playlist.public ? "Public" : "Private"}</span>
                  </div>
                </div>

                {/* Owner and Date */}
                <div className="flex items-center justify-between text-xs text-text-muted pt-1 border-t border-border">
                  <span>By {playlist.owner}</span>
                  {/* <span>{formatDate(playlist.createdAt)}</span> */}
                </div>

                {/* Top Tracks Preview */}
                {playlist.topTracks && playlist.topTracks.length > 0 && (
                  <div className="pt-2 border-t border-border">
                    <p className="text-xs text-text-muted mb-1">Top tracks:</p>
                    <div className="space-y-1">
                      {playlist.topTracks.slice(0, 2).map((track, index) => (
                        <p
                          key={index}
                          className="text-xs text-text-secondary line-clamp-1"
                        >
                          {track.title} • {track.artist}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredPlaylists.length === 0 && (
        <div className="text-center py-12">
          <Icon
            name="Search"
            size={48}
            className="text-text-muted mx-auto mb-4"
          />
          <h3 className="text-lg font-medium text-text-primary mb-2">
            No playlists found
          </h3>
          <p className="text-text-secondary">
            {searchTerm
              ? `No playlists match "${searchTerm}"`
              : "No playlists available with current filters"}
          </p>
          {searchTerm && (
            <Button
              variant="ghost"
              onClick={() => setSearchTerm("")}
              className="mt-4"
            >
              Clear search
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PlaylistGrid;
